import { alpha, Box, Stack, type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import type { PaletteColor } from '@mui/material/styles';
import type { ReactNode } from 'react';

export type StateTone =
  'primary' | 'secondary' | 'accent' | 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface StateDisplayProps {
  /** The illustrative icon shown inside the soft gradient medallion. */
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** Buttons / links rendered under the copy (already-composed elements). */
  actions?: ReactNode;
  /** Color language of the medallion — maps to the shared status/brand palette. */
  tone?: StateTone;
  /** Extra spacing control for embedding inside a card vs. a full page. */
  dense?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * The shared visual scaffold behind every full-surface feedback state (empty, error, loading done,
 * success, no-results, coming-soon). A soft tinted "medallion" holds an illustrative icon above a
 * title, supporting copy and optional actions — one premium, centered layout reused everywhere so
 * the product never shows a bare "No data" string (docs/development/FrontendGuidelines.md §7.10).
 */
export function StateDisplay({
  icon,
  title,
  description,
  actions,
  tone = 'primary',
  dense = false,
  sx,
}: StateDisplayProps) {
  const theme = useTheme();
  const isNeutral = tone === 'neutral';
  const main = isNeutral
    ? theme.palette.text.secondary
    : (theme.palette[tone] as PaletteColor).main;
  const tintTop = isNeutral ? alpha(theme.palette.text.primary, 0.08) : alpha(main, 0.16);
  const tintBottom = isNeutral ? alpha(theme.palette.text.primary, 0.02) : alpha(main, 0.04);

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={dense ? 1.25 : 2}
      sx={{ py: dense ? 4 : 8, px: 3, textAlign: 'center', ...sx }}
    >
      <Box
        sx={{
          width: dense ? 56 : 76,
          height: dense ? 56 : 76,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: main,
          background: `linear-gradient(160deg, ${tintTop} 0%, ${tintBottom} 100%)`,
          '& svg': { fontSize: dense ? 28 : 36 },
        }}
      >
        {icon}
      </Box>
      <Typography variant={dense ? 'h6' : 'h5'} component="h2">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
          {description}
        </Typography>
      ) : null}
      {actions ? (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}
        >
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}
